<template>
	<v-main>
		<v-container fluid fill-height>
			<v-layout justify-center align-center>
				<v-card width="100%" outlined elevation-0>
					<v-toolbar flat class="border_bottom" shaped>
						<v-toolbar-title>Commissioni</v-toolbar-title>
						<v-spacer></v-spacer>
						<v-toolbar-items>
							<v-btn text :loading="syncing" :disabled="!hasApi" title="Rileggi le commissioni del mese dalle API" @click="rileggi">
								<v-icon>mdi-cloud-sync-outline</v-icon>
							</v-btn>
						</v-toolbar-items>
					</v-toolbar>

					<v-col cols="12">
						<v-row>
							<v-col cols="12" md="3">
								<v-text-field v-model="month" type="month" label="Mese" outlined dense hide-details :max="currentMonth" />
							</v-col>
							<v-col cols="12" md="9" class="d-flex align-center fee-muted">
								Dove la fonte ha un'API la commissione arriva da sola. Dove non c'è, inserisci il totale dell'estratto conto del mese oppure la tariffa: il mese nel report si aggiorna da solo.
							</v-col>
						</v-row>
					</v-col>

					<v-simple-table class="fee-table">
						<thead>
							<tr>
								<th style="width: 44px"></th>
								<th>Fonte</th>
								<th class="text-right">Ordini</th>
								<th>Da dove arriva</th>
								<th class="text-right">Commissione del mese</th>
								<th style="width: 170px">Totale manuale</th>
								<th style="width: 230px">Tariffa</th>
							</tr>
						</thead>
						<tbody>
							<template v-for="src in sources">
								<tr :key="src.id">
									<td class="pl-1 pr-0">
										<v-btn
											v-if="src.byType || (!src.connector && src.origin !== 'included')"
											icon
											small
											:title="open[src.id] ? 'Chiudi' : src.connector ? 'Dettaglio delle voci' : 'Modifica la tariffa'"
											@click="toggle(src.id)"
										>
											<v-icon small>{{ open[src.id] ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
										</v-btn>
									</td>
									<td>
										<div class="font-weight-medium">{{ src.connector ? src.connector.label : sourceName(src.key) }}</div>
										<div class="fee-muted">{{ src.scope === 'channel' ? 'Canale di vendita' : 'Metodo di pagamento' }}</div>
									</td>
									<td class="text-right">{{ formatNumber(src.orders) }}</td>
									<td>
										<v-chip x-small label outlined :color="ORIGIN[src.origin].color">{{ ORIGIN[src.origin].label }}</v-chip>
										<div v-if="src.connector && src.sync && src.sync.status === 'error'" class="error--text fee-small mt-1">{{ src.sync.error }}</div>
										<div v-else-if="src.connector && src.sync" class="fee-muted mt-1">Letta il {{ src.sync.syncedAt | moment('DD/MM/YYYY HH:mm') }}</div>
										<div v-else-if="src.connector" class="fee-muted mt-1">Lettura dall'API in coda</div>
									</td>
									<td class="text-right font-weight-medium text-no-wrap">{{ amountOf(src) }}</td>
									<td>
										<v-text-field
											v-model="manual[src.id]"
											type="number"
											step="0.01"
											prefix="€"
											dense
											hide-details
											single-line
											placeholder="—"
											:disabled="src.api !== null || src.origin === 'included' || savingId === src.id"
											:loading="savingId === src.id"
											:title="src.api !== null ? 'Arriva dall\'API: il totale manuale non serve' : 'Totale delle commissioni del mese dall\'estratto conto'"
											@change="salvaManuale(src)"
										/>
									</td>
									<td>
										<div v-if="src.connector" class="fee-muted">Non serve: c'è l'API</div>
										<div v-else-if="src.origin === 'included'" class="fee-muted">Pagamento Stripe: la commissione è nel totale di Stripe</div>
										<div v-else-if="src.rate" class="fee-small">
											{{ rateSummary(src.rate) }}
											<div v-if="src.rate.validFrom" class="fee-muted">dal {{ monthLabel(src.rate.validFrom) }}</div>
										</div>
										<div v-else class="fee-muted">Nessuna: apri con +</div>
									</td>
								</tr>
								<tr v-if="open[src.id] && !src.connector && src.origin !== 'included'" :key="`${src.id}-rate`" class="fee-detail">
									<td></td>
									<td colspan="6">
										<FeeRateEditor :rates="src.rates" :month="month" :saving="savingId === src.id" @cancel="toggle(src.id)" @save="salvaTariffa(src, $event)" />
									</td>
								</tr>
								<tr v-if="open[src.id] && src.byType" :key="`${src.id}-detail`" class="fee-detail">
									<td></td>
									<td colspan="6">
										<div v-for="(value, type) in src.byType" :key="type" class="d-flex fee-small" style="max-width: 480px">
											<span>{{ type }}</span>
											<v-spacer />
											<span>{{ formatEurCents(value) }}</span>
										</div>
										<div v-if="src.shippingLabels" class="d-flex fee-small fee-muted mt-1" style="max-width: 480px">
											<span>Etichette di spedizione comprate sul marketplace (non sono commissioni)</span>
											<v-spacer />
											<span>{{ formatEurCents(src.shippingLabels) }}</span>
										</div>
										<div v-if="src.unconverted" class="fee-small orange--text text--darken-2 mt-1">{{ src.unconverted }} voci in valute senza cambio nel profilo: escluse dal totale.</div>
									</td>
								</tr>
							</template>
							<tr v-if="!loading && !sources.length">
								<td colspan="7" class="text-center grey--text py-6">Nessun ordine e nessuna fonte collegata in questo mese.</td>
							</tr>
						</tbody>
					</v-simple-table>

					<div class="pa-3 border_top fee-muted">
						Priorità: API, poi totale manuale, poi tariffa. La tariffa vale per tutti i mesi dal mese indicato: salvandola, il mese che stai guardando si ricalcola; gli altri mesi già fotografati con
						«Ricalcola mese» nel report.
					</div>
				</v-card>
			</v-layout>
		</v-container>
	</v-main>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import FeeRateEditor from '@/components/finance/FeeRateEditor.vue';
import { formatEurCents, formatNumber, previousMonth, monthKey, monthLabel, rateSummary, sourceName, FEE_ORIGIN as ORIGIN } from '@/components/finance/format';

const POLL_MS = 5000;
const POLL_ROUNDS = 24;

export default {
	name: 'FinanceFees',
	components: { FeeRateEditor },
	data() {
		return {
			ORIGIN,
			month: /^\d{4}-\d{2}$/.test(this.$route.query.month || '') ? this.$route.query.month : previousMonth(),
			currentMonth: monthKey(),
			sources: [],
			manual: {},
			settings: null,
			open: {},
			loading: false,
			syncing: false,
			savingId: null,
		};
	},
	computed: {
		hasApi() {
			return this.sources.some(s => s.connector);
		},
	},
	watch: {
		month(value) {
			if (this.$route.query.month !== value) this.$router.replace({ query: { month: value } }).catch(() => {});
			this.carica();
		},
	},
	async mounted() {
		try {
			const res = await this.fetchSettings();
			this.settings = res.settings;
		} catch (err) {
			this.errore(err);
		}
		await this.carica();
	},
	beforeDestroy() {
		clearTimeout(this.pollTimer);
	},
	methods: {
		...mapMutations({ setLogout: 'profile/setLogout' }),
		...mapActions({
			fetchFees: 'finance/fetchFees',
			saveManualFee: 'finance/saveManualFee',
			syncFees: 'finance/syncFees',
			fetchSettings: 'finance/fetchSettings',
			saveSettings: 'finance/saveSettings',
			recompute: 'finance/recompute',
		}),
		formatEurCents,
		formatNumber,
		monthLabel,
		rateSummary,
		sourceName,
		amountOf(src) {
			if (src.api !== null) return formatEurCents(src.api);
			if (src.manual !== null) return formatEurCents(src.manual);
			if (src.origin === 'estimate') return 'dalla tariffa';
			if (src.origin === 'nofee') return formatEurCents(0);
			return '—';
		},
		applica(data) {
			this.sources = data.sources;
			const manual = {};
			for (const src of data.sources) manual[src.id] = src.manual === null ? '' : src.manual;
			this.manual = manual;
		},
		async carica() {
			this.loading = true;
			try {
				this.applica(await this.fetchFees(this.month));
			} catch (err) {
				this.errore(err);
			} finally {
				this.loading = false;
			}
		},
		async salvaManuale(src) {
			const value = this.manual[src.id];
			this.savingId = src.id;
			try {
				this.applica(await this.saveManualFee({ month: this.month, scope: src.scope, key: src.key, amount: value === '' ? null : +value }));
				this.notify('success', value === '' ? 'Totale manuale tolto' : 'Totale salvato: il mese nel report è aggiornato');
			} catch (err) {
				this.errore(err);
			} finally {
				this.savingId = null;
			}
		},
		// listino della fonte: sostituisce le sue versioni, le tariffe delle altre fonti restano come sono
		async salvaTariffa(src, versions) {
			this.savingId = src.id;
			try {
				const settings = await this.fetchSettings();
				const others = (settings.settings.rates || []).filter(r => !(r.scope === src.scope && String(r.key).toLowerCase() === String(src.key).toLowerCase()));
				await this.saveSettings({ rates: [...others, ...versions.map(v => ({ ...v, scope: src.scope, key: src.key }))] });
				// il mese che si sta guardando si aggiorna subito (quello in corso non ha fotografia)
				if (this.month < this.currentMonth) await this.recompute(this.month);
				this.notify('success', 'Tariffa salvata');
				this.$set(this.open, src.id, false);
				await this.carica();
			} catch (err) {
				this.errore(err);
			} finally {
				this.savingId = null;
			}
		},
		// la lettura gira in coda: si ricarica finche' tutte le fonti API hanno un esito piu' recente
		async rileggi() {
			const startedAt = Date.now();
			this.syncing = true;
			try {
				await this.syncFees(this.month);
				this.notify('success', 'Lettura delle commissioni avviata');
				this.poll(startedAt, 0);
			} catch (err) {
				this.syncing = false;
				this.errore(err);
			}
		},
		poll(startedAt, round) {
			this.pollTimer = setTimeout(async () => {
				await this.carica();
				const pending = this.sources.some(s => s.connector && (!s.sync || new Date(s.sync.syncedAt).getTime() < startedAt));
				if (pending && round < POLL_ROUNDS) this.poll(startedAt, round + 1);
				else this.syncing = false;
			}, POLL_MS);
		},
		toggle(id) {
			this.$set(this.open, id, !this.open[id]);
		},
		notify(color, text) {
			this.$store.dispatch('settings/updateSnackbar', { color, text, snackbar: true, mode: 'multi-line', timeout: 5000 });
		},
		errore(err) {
			const res = err.response;
			if (res && res.status === 401) {
				this.setLogout();
				this.$router.push('/login');
				return;
			}
			const errors = res && res.data && res.data.error;
			this.notify('error', Array.isArray(errors) ? errors.map(e => e.msg).join(', ') : (res && res.data && res.data.message) || err.message);
		},
	},
};
</script>

<style scoped>
.fee-muted {
	font-size: 12px;
	color: #5f6b7a;
}
.fee-small {
	font-size: 12px;
}
.fee-detail td {
	background: #f7f9fc;
}
.border_top {
	border-top: 1px solid #e1e7ef;
}
</style>
